import { Inject, Injectable, Optional } from '@angular/core';
import { concat, defer, firstValueFrom, Observable, shareReplay, Subject } from 'rxjs';
import { DashboardPreferences } from './dashboard-preferences';
import { DASHBOARD_PREFERENCES_STORAGE } from './dashboard-preferences-storage.injection-token';
import { DASHBOARD_PREFERENCES_TTL } from './dashboard-preferences-ttl.injection-token';

@Injectable({
  providedIn: 'root',
})
export class DashboardPreferencesService {
  static readonly defaultWorklist = 'unresolved';
  static readonly defaultStartDateOffsetDays = 7;
  static readonly storageKey = 'voice-ai.dashboard.preferences';

  private static readonly msPerMinute = 60 * 1000;

  private readonly storageService: Storage;
  private readonly updates = new Subject<DashboardPreferences>();
  readonly preferences$: Observable<DashboardPreferences>;

  constructor(@Inject(DASHBOARD_PREFERENCES_TTL) private readonly ttl: number, @Optional() @Inject(DASHBOARD_PREFERENCES_STORAGE) storageService: Storage) {
    this.storageService = storageService !== null ? storageService : sessionStorage;

    const initial$ = defer(() => {
      const raw = this.storageService.getItem(DashboardPreferencesService.storageKey);
      const { _savedAt, ...stored } = JSON.parse(raw ?? '{}');
      const now = new Date();
      const isExpired = _savedAt && now.getTime() - new Date(_savedAt).getTime() > this.ttl * DashboardPreferencesService.msPerMinute;
      const effective = isExpired ? {} : stored;
      const defaultStartDate = new Date(now);
      defaultStartDate.setDate(defaultStartDate.getDate() - DashboardPreferencesService.defaultStartDateOffsetDays);
      const preferences: DashboardPreferences = {
        ...effective,
        worklist: effective.worklist ?? DashboardPreferencesService.defaultWorklist,
        startDate: effective.startDate ? new Date(effective.startDate) : defaultStartDate,
        endDate: effective.endDate ? new Date(effective.endDate) : now,
      };
      return [preferences];
    });
    this.preferences$ = concat(initial$, this.updates).pipe(shareReplay(1));
  }

  updatePreferences(changes: Partial<DashboardPreferences>): Observable<void> {
    return defer(async () => {
      const now = new Date();
      const current = await firstValueFrom(this.preferences$);
      const updated = { ...current, ...changes };
      const persisted = { ...updated, _savedAt: now.toISOString() };
      this.storageService.setItem(DashboardPreferencesService.storageKey, JSON.stringify(persisted));
      this.updates.next(updated);
    });
  }
}
